import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { isNumber } from 'es-toolkit/compat';
import { atom, useAtomValue } from 'jotai';

import { FundingTypeEnum } from '@flipster/apis/v2/snapshot';
import { FundingTypeTextMapper, FundingTypeTimeMapper } from '@flipster/apis/v2/snapshot/constants';
import { FormattedMessage, i18nBunja } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { CountdownDate } from '@flipster/react/design-utils/countdown-date';
import Formatted from '@flipster/react/design-utils/formatted';
import { hstack } from '@flipster/styled-system/patterns';
// ROLLBACK(last-price): ChartInvertedPrice(QA-21025) 재활성화 시 아래 import 복원.
// import { type MessageKey } from '@flipster/i18n';
// import {
//   getChartLinkedTickerPrice,
//   invertChartPriceType,
//   normalizeChartPriceType,
//   type ChartPriceType,
// } from '@flipster/tvchart/src/misc/chart-price-type';
// import { getTradingViewDataStorageAtom } from '@flipster/tvchart/src/misc/data-manager';
import { FundingRatePrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { perpetualSymbolSocketBunja, tradeSymbolBunja } from '../../symbol';
import { Ddd } from './ddd';
import MarketStatus from './market-status';
import { RightLayout } from './right-layout';

export const SymbolRightPerpetual = () => {
  const { canRenderSymbolRightPerpetualAtom } = useBunja(symbolRightPerpetualBunja);
  const canRenderSymbolRightPerpetual = useAtomValue(canRenderSymbolRightPerpetualAtom);
  if (!canRenderSymbolRightPerpetual) return;

  return (
    <RightLayout>
      <MarketStatus />
      {/* ROLLBACK(last-price): 원본은 <ChartInvertedPrice /> (QA-21025 — 차트와 반대 소스 표시) */}
      <MarkPrice />
      <IndexPrice />
      <FundingRate />
      <High24h />
      <Low24h />
      <Volume24h />
      <Turnover24h />
      <OpenInterest />
    </RightLayout>
  );
};

const symbolRightPerpetualBunja = bunja(() => {
  const { fundingTypeAtom, positionUnitAtom, pricePrecisionAtom, quoteCurrencyAtom } = bunja.use(tradeSymbolBunja);
  const { getTickerAtom } = bunja.use(perpetualSymbolSocketBunja);
  const { getT } = bunja.use(i18nBunja);
  const t = getT();

  // ROLLBACK(last-price): ChartInvertedPrice 재활성화 시 아래 atom들 복원.
  // const chartPriceTypeAtom = getTradingViewDataStorageAtom('priceType');
  // const chartInvertedPriceTypeAtom = atom((get) =>
  //   invertChartPriceType(normalizeChartPriceType(get(chartPriceTypeAtom))),
  // );
  // const chartInvertedPriceAtom = atom((get) => {
  //   const ticker = get(getTickerAtom);
  //   if (!ticker) return undefined;
  //   return getChartLinkedTickerPrice(ticker, get(chartInvertedPriceTypeAtom));
  // });

  const canRenderSymbolRightPerpetualAtom = atom((get) => Boolean(get(getTickerAtom)));
  const markPriceAtom = atom((get) => get(getTickerAtom)?.markPrice);
  const indexPriceAtom = atom((get) => get(getTickerAtom)?.indexPrice);
  const high24hAtom = atom((get) => get(getTickerAtom)?.high24h);
  const low24hAtom = atom((get) => get(getTickerAtom)?.low24h);
  const volume24hAtom = atom((get) => get(getTickerAtom)?.volume24h);
  const turnover24hAtom = atom((get) => get(getTickerAtom)?.turnover24h);
  const openInterestInUsdtAtom = atom((get) => get(getTickerAtom)?.openInterestInUsdt);
  const fundingRateAtom = atom((get) => get(getTickerAtom)?.fundingRate);
  const fundingTimeAtom = atom((get) => get(getTickerAtom)?.fundingTime);
  const fundingCycleAtom = atom((get) => {
    const fundingType = get(fundingTypeAtom);
    return FundingTypeTimeMapper[fundingType ?? FundingTypeEnum.ImmediateHour8];
  });
  const fundingTypeTextAtom = atom((get) => {
    const fundingType = get(fundingTypeAtom);
    return t('Autogen_common.r251126.s_Funding_FundingTypeTextMapper', {
      FundingTypeTextMapper: FundingTypeTextMapper[fundingType ?? FundingTypeEnum.ImmediateHour8],
    });
  });
  const annualizedFundingRateAtom = atom((get) => {
    const fundingRate = get(fundingRateAtom);
    const fundingCycle = get(fundingCycleAtom);
    if (!fundingRate || !isNumber(fundingCycle)) return;
    return Num(fundingRate).multipliedBy(fundingCycle).multipliedBy(365).toString();
  });

  return {
    canRenderSymbolRightPerpetualAtom,
    positionUnitAtom,
    pricePrecisionAtom,
    quoteCurrencyAtom,
    // chartInvertedPriceTypeAtom, // ROLLBACK(last-price)
    // chartInvertedPriceAtom, // ROLLBACK(last-price)
    markPriceAtom,
    indexPriceAtom,
    high24hAtom,
    low24hAtom,
    volume24hAtom,
    turnover24hAtom,
    openInterestInUsdtAtom,
    fundingRateAtom,
    fundingTimeAtom,
    fundingCycleAtom,
    fundingTypeTextAtom,
    annualizedFundingRateAtom,
  };
});
symbolRightPerpetualBunja.debugLabel = 'symbolRightPerpetualBunja';

// ROLLBACK(last-price): QA-21025 — 차트 소스와 반대되는 가격(Last↔Mark)을 표시하던 컴포넌트.
// MM 준비 완료 시 아래 블록과 관련 atom/import를 복원하고 <MarkPrice /> 대신 렌더할 것.
// const chartInvertedPriceLabelId = {
//   LAST: 'Common.r260623.Last_price',
//   MARK: 'Assets.r250401.Mark_price',
// } as const satisfies Record<ChartPriceType, MessageKey>;
//
// const ChartInvertedPrice = () => {
//   const { chartInvertedPriceTypeAtom, chartInvertedPriceAtom, pricePrecisionAtom } =
//     useBunja(symbolRightPerpetualBunja);
//   const chartInvertedPriceType = useAtomValue(chartInvertedPriceTypeAtom);
//   const chartInvertedPrice = useAtomValue(chartInvertedPriceAtom);
//   const pricePrecision = useAtomValue(pricePrecisionAtom);
//
//   return (
//     <Ddd
//       dt={<FormattedMessage id={chartInvertedPriceLabelId[chartInvertedPriceType]} />}
//       dd={<Formatted.Number value={chartInvertedPrice} precision={pricePrecision} toFixed />}
//     />
//   );
// };

const MarkPrice = () => {
  const { markPriceAtom, pricePrecisionAtom } = useBunja(symbolRightPerpetualBunja);
  const markPrice = useAtomValue(markPriceAtom);
  const pricePrecision = useAtomValue(pricePrecisionAtom);

  return (
    <Ddd
      dt={<FormattedMessage id='Autogen_common.r251126.s_Mark' />}
      dd={<Formatted.Number value={markPrice} precision={pricePrecision} toFixed />}
    />
  );
};

const IndexPrice = () => {
  const { indexPriceAtom, pricePrecisionAtom } = useBunja(symbolRightPerpetualBunja);
  const indexPrice = useAtomValue(indexPriceAtom);
  const pricePrecision = useAtomValue(pricePrecisionAtom);

  return (
    <Ddd
      dt={<FormattedMessage id='Autogen_common.r251126.s_Index' />}
      dd={<Formatted.Number value={indexPrice} precision={pricePrecision} toFixed />}
    />
  );
};

const FundingRate = () => {
  const { fundingRateAtom, fundingTimeAtom, fundingTypeTextAtom, annualizedFundingRateAtom } =
    useBunja(symbolRightPerpetualBunja);
  const fundingRate = useAtomValue(fundingRateAtom);
  const fundingTime = useAtomValue(fundingTimeAtom);
  const fundingTypeText = useAtomValue(fundingTypeTextAtom);
  const annualizedFundingRate = useAtomValue(annualizedFundingRateAtom);

  return (
    <Ddd
      dt={fundingTypeText}
      dd={
        <div className={hstack({ gap: 4 })}>
          <Tooltip placement='bottom'>
            <Tooltip.Trigger>
              <Formatted.Percent
                value={fundingRate}
                precision={FundingRatePrecision}
                toFixed
                isRate
                css={{ textStyle: 'Text/M/Monospace/Underline', cursor: 'pointer' }}
              />
            </Tooltip.Trigger>
            <Tooltip.Content portal>
              {'Annualized '}
              <Formatted.Percent value={annualizedFundingRate} precision={FundingRatePrecision} toFixed isRate />
            </Tooltip.Content>
          </Tooltip>
          {fundingTime && <CountdownDate targetDate={fundingTime} />}
        </div>
      }
    />
  );
};

const High24h = () => {
  const { high24hAtom, pricePrecisionAtom } = useBunja(symbolRightPerpetualBunja);
  const high24h = useAtomValue(high24hAtom);
  const pricePrecision = useAtomValue(pricePrecisionAtom);

  return (
    <Ddd
      dt={<FormattedMessage id='Autogen_common.r251126.s_24h_high' />}
      dd={<Formatted.Number value={high24h} precision={pricePrecision} toFixed />}
    />
  );
};

const Low24h = () => {
  const { low24hAtom, pricePrecisionAtom } = useBunja(symbolRightPerpetualBunja);
  const low24h = useAtomValue(low24hAtom);
  const pricePrecision = useAtomValue(pricePrecisionAtom);

  return (
    <Ddd
      dt={<FormattedMessage id='Autogen_common.r251126.s_24h_low' />}
      dd={<Formatted.Number value={low24h} precision={pricePrecision} toFixed />}
    />
  );
};

const Volume24h = () => {
  const { volume24hAtom, positionUnitAtom } = useBunja(symbolRightPerpetualBunja);
  const volume24h = useAtomValue(volume24hAtom);
  const positionUnit = useAtomValue(positionUnitAtom);

  return (
    <Ddd
      dt={
        <FormattedMessage
          id='Autogen_common.r251126.s_24h_volume_symbolInfo_positionUnit'
          values={{ symbolInfo_positionUnit: positionUnit }}
        />
      }
      dd={<Formatted.ShortNumber value={volume24h} toFixed zeroString='-' />}
    />
  );
};

const Turnover24h = () => {
  const { turnover24hAtom, quoteCurrencyAtom } = useBunja(symbolRightPerpetualBunja);
  const turnover24h = useAtomValue(turnover24hAtom);
  const quoteCurrency = useAtomValue(quoteCurrencyAtom);

  return (
    <Ddd
      dt={
        <FormattedMessage
          id='Autogen_common.r251126.s_24h_turnover_symbolInfo_quoteCurrency'
          values={{ symbolInfo_quoteCurrency: quoteCurrency }}
        />
      }
      dd={<Formatted.ShortNumber value={turnover24h} toFixed zeroString='-' />}
    />
  );
};

const OpenInterest = () => {
  const { openInterestInUsdtAtom, quoteCurrencyAtom } = useBunja(symbolRightPerpetualBunja);
  const openInterestInUsdt = useAtomValue(openInterestInUsdtAtom);
  const quoteCurrency = useAtomValue(quoteCurrencyAtom);

  return (
    <Ddd
      dt={
        <FormattedMessage
          id='Autogen_common.r251126.s_Open_interest_symbolInfo_quoteCurrency'
          values={{ symbolInfo_quoteCurrency: quoteCurrency }}
        />
      }
      dd={<Formatted.ShortNumber value={openInterestInUsdt} toFixed zeroString='-' />}
    />
  );
};
